import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { costsAPI, propertiesAPI } from '../lib/api';
import { formatDate, formatCurrency, COST_TYPE_LABELS, DISTRIBUTION_KEY_LABELS } from '../lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  Receipt,
  Edit,
  Trash2,
  Loader2,
  Search,
  Building2,
  Calendar,
  Paperclip
} from 'lucide-react';
import { CostFilesDialog } from '../components/CostFilesDialog';

export default function Costs() {
  const [costs, setCosts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPropertyId, setFilterPropertyId] = useState('all');
  const [filterCostType, setFilterCostType] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [filesCost, setFilesCost] = useState(null);
  const [formData, setFormData] = useState({
    property_id: '',
    cost_type: 'wasser',
    description: '',
    amount: '',
    period_start: '',
    period_end: '',
    invoice_date: '',
    distribution_key: 'wohnflaeche',
    consumption_share: '',  // empty => backend default (0.7 for Heizung/Warmwasser)
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [costsRes, propsRes] = await Promise.all([
        costsAPI.getAll(),
        propertiesAPI.getAll()
      ]);
      setCosts(costsRes.data);
      setProperties(propsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    const currentYear = new Date().getFullYear();
    setFormData({
      property_id: properties.length > 0 ? properties[0].id : '',
      cost_type: 'wasser',
      description: '',
      amount: '',
      period_start: `${currentYear}-01-01`,
      period_end: `${currentYear}-12-31`,
      invoice_date: new Date().toISOString().slice(0, 10),
      distribution_key: 'wohnflaeche',
      consumption_share: '',
    });
    setEditingCost(null);
  };

  const openEditDialog = (cost) => {
    setEditingCost(cost);
    setFormData({
      property_id: cost.property_id,
      cost_type: cost.cost_type,
      description: cost.description,
      amount: cost.amount || '',
      period_start: cost.period_start?.slice(0, 10) || '',
      period_end: cost.period_end?.slice(0, 10) || '',
      invoice_date: cost.invoice_date?.slice(0, 10) || '',
      distribution_key: cost.distribution_key,
      consumption_share: cost.consumption_share != null ? String(Math.round(cost.consumption_share * 100)) : '',
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { consumption_share, ...rest } = formData;
      const data = {
        ...rest,
        amount: parseFloat(formData.amount),
        consumption_share:
          consumption_share === '' || consumption_share == null
            ? null
            : parseFloat(consumption_share) / 100,
      };

      if (editingCost) {
        await costsAPI.update(editingCost.id, data);
        toast.success('Kosten aktualisiert');
      } else {
        await costsAPI.create(data);
        toast.success('Kosten erstellt');
      }

      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await costsAPI.delete(deleteId);
      toast.success('Kosten gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteId(null);
    }
  };

  const getPropertyName = (propertyId) => {
    const prop = properties.find(p => p.id === propertyId);
    return prop ? prop.name : 'Unbekannt';
  };

  const filteredCosts = costs.filter(c => {
    const matchesSearch = c.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProperty = filterPropertyId === 'all' || c.property_id === filterPropertyId;
    const matchesCostType = filterCostType === 'all' || c.cost_type === filterCostType;
    return matchesSearch && matchesProperty && matchesCostType;
  });

  // Calculate total
  const totalCosts = filteredCosts.reduce((sum, c) => sum + c.amount, 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="costs-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Kosten
            </h1>
            <p className="text-slate-500 mt-1">Verwalten Sie Ihre Betriebskosten</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-cost-button" 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={properties.length === 0}
              >
                <Plus className="mr-2 h-4 w-4" />
                Kosten hinzufügen
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>
                  {editingCost ? 'Kosten bearbeiten' : 'Neue Kosten'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="property_id">
                    Immobilie <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.property_id}
                    onValueChange={(value) => setFormData({ ...formData, property_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="cost-property-select">
                      <SelectValue placeholder="Immobilie auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map((prop) => (
                        <SelectItem key={prop.id} value={prop.id}>
                          {prop.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cost_type">
                      Kostenart <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.cost_type}
                      onValueChange={(value) => setFormData({ ...formData, cost_type: value })}
                    >
                      <SelectTrigger data-testid="cost-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(COST_TYPE_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="distribution_key">
                      Verteilerschlüssel <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.distribution_key}
                      onValueChange={(value) => setFormData({ ...formData, distribution_key: value })}
                    >
                      <SelectTrigger data-testid="cost-distribution-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(DISTRIBUTION_KEY_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.distribution_key === 'verbrauch' && ['heizung', 'warmwasser'].includes(formData.cost_type) && (
                    <div className="col-span-2 space-y-2 p-3 rounded-md bg-emerald-50/60 border border-emerald-200">
                      <Label htmlFor="consumption_share" className="text-sm">
                        HeizkostenV-Split: Verbrauchsanteil in %
                      </Label>
                      <div className="flex items-center gap-3">
                        <Input
                          id="consumption_share"
                          data-testid="cost-consumption-share-input"
                          type="number"
                          min="50"
                          max="70"
                          step="1"
                          value={formData.consumption_share}
                          onChange={(e) => setFormData({ ...formData, consumption_share: e.target.value })}
                          placeholder="70 (Standard)"
                          className="w-32"
                        />
                        <span className="text-xs text-slate-600">
                          Rest wird nach Wohnfläche verteilt. § 7 HeizkostenV erlaubt 50–70 %. Leer = 70 % Standard.
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="description">
                      Beschreibung <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="description"
                      data-testid="cost-description-input"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="z.B. Stadtwerke Wasserabrechnung 2024"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="amount">
                      Betrag (€) <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      data-testid="cost-amount-input"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="1250.00"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invoice_date">
                      Rechnungsdatum <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="invoice_date"
                      type="date"
                      data-testid="cost-invoice-date-input"
                      value={formData.invoice_date}
                      onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="period_start">
                      Zeitraum von <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="period_start"
                      type="date"
                      data-testid="cost-period-start-input"
                      value={formData.period_start}
                      onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="period_end">
                      Zeitraum bis <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="period_end"
                      type="date"
                      data-testid="cost-period-end-input"
                      value={formData.period_end}
                      onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Abbrechen
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="cost-save-button">
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Speichern...
                      </>
                    ) : (
                      'Speichern'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="cost-search-input"
              placeholder="Suchen nach Beschreibung..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterPropertyId} onValueChange={setFilterPropertyId}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="cost-filter-property">
              <SelectValue placeholder="Alle Immobilien" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Immobilien</SelectItem>
              {properties.map((prop) => (
                <SelectItem key={prop.id} value={prop.id}>
                  {prop.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterCostType} onValueChange={setFilterCostType}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="cost-filter-type">
              <SelectValue placeholder="Alle Kostenarten" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kostenarten</SelectItem>
              {Object.entries(COST_TYPE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Total Banner */}
        {filteredCosts.length > 0 && (
          <Card className="bg-slate-900 text-white border-0">
            <CardContent className="py-4 flex items-center justify-between">
              <span className="text-slate-300">Gesamtkosten (gefiltert)</span>
              <span className="text-2xl font-bold tabular-nums" data-testid="costs-total">
                {formatCurrency(totalCosts)}
              </span>
            </CardContent>
          </Card>
        )}

        {/* No Properties Warning */}
        {properties.length === 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-4">
              <p className="text-amber-800 text-sm">
                <Building2 className="inline h-4 w-4 mr-2" />
                Erstellen Sie zuerst eine Immobilie, um Kosten hinzufügen zu können.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Costs List */}
        {filteredCosts.length > 0 ? (
          <Card className="border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold">Beschreibung</TableHead>
                  <TableHead className="font-semibold">Immobilie</TableHead>
                  <TableHead className="font-semibold">Kostenart</TableHead>
                  <TableHead className="font-semibold">Zeitraum</TableHead>
                  <TableHead className="font-semibold text-right">Betrag</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCosts.map((cost) => (
                  <TableRow key={cost.id} data-testid={`cost-row-${cost.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                          <Receipt className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{cost.description}</p>
                          <p className="text-sm text-slate-500">
                            {DISTRIBUTION_KEY_LABELS[cost.distribution_key]}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {getPropertyName(cost.property_id)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                        {COST_TYPE_LABELS[cost.cost_type] || cost.cost_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Calendar className="h-4 w-4 text-slate-400" />
                        {formatDate(cost.period_start)} - {formatDate(cost.period_end)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-semibold tabular-nums text-slate-900">
                        {formatCurrency(cost.amount)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`files-cost-${cost.id}`}
                          onClick={() => setFilesCost(cost)}
                          title="Belege"
                        >
                          <Paperclip className="h-4 w-4 text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`edit-cost-${cost.id}`}
                          onClick={() => openEditDialog(cost)}
                        >
                          <Edit className="h-4 w-4 text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`delete-cost-${cost.id}`}
                          onClick={() => setDeleteId(cost.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <Receipt className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {searchTerm || filterPropertyId !== 'all' || filterCostType !== 'all' 
                  ? 'Keine Kosten gefunden' 
                  : 'Noch keine Kosten'
                }
              </h3>
              <p className="text-slate-500 mb-4">
                {searchTerm || filterPropertyId !== 'all' || filterCostType !== 'all'
                  ? 'Versuchen Sie andere Filtereinstellungen' 
                  : 'Erfassen Sie Ihre ersten Betriebskosten'
                }
              </p>
              {!searchTerm && filterPropertyId === 'all' && filterCostType === 'all' && properties.length > 0 && (
                <Button onClick={() => setIsDialogOpen(true)} data-testid="empty-add-cost-button">
                  <Plus className="mr-2 h-4 w-4" />
                  Erste Kosten erfassen
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Kosten löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Diese Aktion kann nicht rückgängig gemacht werden. Der Kosteneintrag 
                wird dauerhaft entfernt.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-cost"
              >
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Cost Files Dialog */}
        <CostFilesDialog
          cost={filesCost}
          open={!!filesCost}
          onOpenChange={(open) => !open && setFilesCost(null)}
        />
      </div>
    </Layout>
  );
}
